import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { useMemo, useRef } from "react";
import type { RouterContext } from "./router/routes/__root.js";
import { routeTree } from "./router/routeTree.gen.js";
import { createTrpcClient } from "./trpc/client.js";
import type { SupportEnv } from "./config/env.js";

/**
 * Top-level React component for the staff support console.
 *
 * Standard provider stack (`<ClerkProvider>` → `<QueryClientProvider>`
 * → `<RouterProvider>`) with no crash-reporter seam — this is a
 * low-traffic internal surface, so the router context carries just the
 * tRPC client and the query cache.
 */
export type AppProps = {
  readonly env: SupportEnv;
};

export function App({ env }: AppProps) {
  return (
    <ClerkProvider publishableKey={env.clerkPublishableKey}>
      {env.clerkPublishableKeySource === "development-placeholder" ? (
        <DevPlaceholderAuthBanner />
      ) : null}
      <AppInner env={env} />
    </ClerkProvider>
  );
}

function DevPlaceholderAuthBanner() {
  return (
    <div
      role="status"
      className="border-b border-warning bg-warning/20 px-4 py-2 text-sm text-fg"
    >
      <strong>Dev mode:</strong> Clerk auth is in placeholder mode (auth
      won&apos;t work). Set <code>VITE_CLERK_PUBLISHABLE_KEY</code> in{" "}
      <code>apps/support/.env.local</code> and restart Vite to enable sign-in.
    </div>
  );
}

function AppInner({ env }: AppProps) {
  // `useAuth().getToken` is a fresh reference on every Clerk state
  // tick. Holding the latest reference in a ref (and keeping it out of
  // the `useMemo` deps) means the router / queryClient are built once
  // at mount, not torn down and rebuilt on every session-refresh tick.
  const { getToken } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const { router, queryClient } = useMemo(() => {
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 30_000,
          refetchOnWindowFocus: false,
        },
      },
    });
    const trpc = createTrpcClient({
      apiBaseUrl: env.apiBaseUrl,
      getToken: () => getTokenRef.current(),
    });
    const router = createRouter({
      routeTree,
      context: {
        trpc,
        queryClient,
        clerkDashboardUserUrlTemplate: env.clerkDashboardUserUrlTemplate,
      } satisfies RouterContext,
      defaultPreload: "intent",
    });
    return { router, queryClient };
  }, [env.apiBaseUrl, env.clerkDashboardUserUrlTemplate]);

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createRouterForTypeRegistration>;
  }
}

// Helper used purely to anchor the `Register` declaration above to the
// actual router shape; never invoked at runtime.
function createRouterForTypeRegistration() {
  return createRouter({
    routeTree,
    context: {} as RouterContext,
  });
}
